import { EDifficultyType, ELightGalleryType, EOsceType, EPaceType } from '@quesmed/types/models';
import { z } from 'zod';
import { assetSchema, type IValidateAsset, pictureSchema, validateFieldWithAssets } from '@/config/asset';

export interface IAiConfigLlm {
  model?: string;
  temperature?: string;
  provider?: string;
  reasoning_effort?: 'low' | 'medium' | 'high' | 'none' | 'minimal';
  reasoning_summary?: 'auto' | 'concise' | 'detailed';
  max_completion_tokens?: string;
  /** Registry prompt ID (`Prompt.id`). */
  promptId?: string;
  /** Registry prompt ID (`Prompt.id`) for the user prompt — markscheme/result phases only. */
  userPromptId?: string;
  /** Conversation transcript mode fed to the LLM — markscheme/result phases only. */
  transcriptMode?: 'full' | 'user_only';
}

export interface IAiConfigSttDeepgram {
  model?: string;
  language?: string;
  smart_format?: boolean;
  interim_results?: boolean;
  enable_diarization?: boolean;
  endpointing?: string;
  keyterms?: string;
}

export interface IAiConfigSttOpenAi {
  model?: string;
  language?: string;
  temperature?: string;
  prompt?: string;
}

export interface IAiConfigSttAssemblyAi {
  model?: string;
  language?: string;
  speaker_labels?: boolean;
  keyterms_prompt?: string;
  min_turn_silence?: string;
  max_turn_silence?: string;
  vad_threshold?: string;
}

export interface IAiConfigSttXaiSettings {
  model?: string;
  language?: string;
  enable_diarization?: boolean;
  keyterms_prompt?: string;
  min_turn_silence?: string;
  max_turn_silence?: string;
  vad_threshold?: string;
}

export interface IAiConfigStt {
  provider?: string;
  deepgram?: IAiConfigSttDeepgram;
  openai?: IAiConfigSttOpenAi;
  assemblyai?: IAiConfigSttAssemblyAi;
  xai?: IAiConfigSttXaiSettings;
}

export interface IAiConfigTtsElevenLabsSettings {
  model?: string;
  voice?: string;
  stability?: string;
  similarity_boost?: string;
  style?: string;
  speed?: string;
  use_speaker_boost?: boolean;
}

export interface IAiConfigTtsCartesiaSettings {
  model?: string;
  voice?: string;
  speed?: string;
  volume?: string;
  emotion?: string;
}

export interface IAiConfigTtsOpenAiSettings {
  model?: string;
  voice?: string;
  speed?: string;
  instructions?: string;
}

export interface IAiConfigTtsDeepgramSettings {
  model?: string;
  voice?: string;
  speed?: string;
}

export interface IAiConfigTtsMiniMaxSettings {
  model?: string;
  voice?: string;
  speed?: string;
  pitch?: string;
  vol?: string;
  emotion?: string;
}

export interface IAiConfigTtsXaiSettings {
  model?: string;
  voice?: string;
}

export interface IAiConfigTtsRimeSettings {
  model?: string;
  voice?: string;
  speed?: string;
}

export interface IAiConfigTtsInworldSettings {
  model?: string;
  voice?: string;
  speaking_rate?: string;
  temperature?: string;
}

export interface IAiConfigTtsFishAudioSettings {
  model?: string;
  voice?: string;
  speed?: string;
  volume?: string;
  latency_mode?: string;
}

export interface IAiConfigTtsGradiumSettings {
  model?: string;
  voice?: string;
}

export interface IAiConfigTtsGeminiSettings {
  model?: string;
  voice?: string;
}
export interface IAiConfigTts {
  provider?: string;
  elevenlabs?: IAiConfigTtsElevenLabsSettings;
  cartesia?: IAiConfigTtsCartesiaSettings;
  openai?: IAiConfigTtsOpenAiSettings;
  deepgram?: IAiConfigTtsDeepgramSettings;
  minimax?: IAiConfigTtsMiniMaxSettings;
  rime?: IAiConfigTtsRimeSettings;
  inworld?: IAiConfigTtsInworldSettings;
  xai?: IAiConfigTtsXaiSettings;
  fishaudio?: IAiConfigTtsFishAudioSettings;
  gradium?: IAiConfigTtsGradiumSettings;
  gemini?: IAiConfigTtsGeminiSettings;
}

export interface IAiConfigPhase {
  llm?: IAiConfigLlm;
  stt?: IAiConfigStt;
  tts?: IAiConfigTts;
}

export interface IAgentConfig {
  entryAgent?: 'patient' | 'examination' | 'examiner';
  multiAgent?: boolean;
}

export interface IAiConfig {
  patient?: IAiConfigPhase;
  examiner?: IAiConfigPhase;
  examination?: IAiConfigPhase;
  markscheme?: IAiConfigPhase;
  result?: IAiConfigPhase;
  agentConfig?: IAgentConfig;
  // turn_router?: IAiConfigPhase;
}

export type AiConfigPhaseKey = 'patient' | 'examiner' | 'examination' | 'markscheme' | 'result';
// | 'turn_router';
export type AiConfigBaseName = `aiConfig.${AiConfigPhaseKey}`;

// Phase briefs — disabled until backend supports persistence
// export interface IPhaseBrief {
//   title: string;
//   action: 'patient_agent' | 'examiner_agent' | 'next';
//   timer: number;
//   content: string;
// }
//
// const phaseBriefSchema = z.object({
//   title: z.string(),
//   action: z.enum(['patient_agent', 'examiner_agent', 'next']),
//   timer: z.number(),
//   content: z.string(),
// });

const phaseTimersSchema = z.object({
  introPhase: z.number().int(),
  readingPhase: z.number().int(),
  stationPhase: z.number().int(),
  examinerPhase: z.number().int(),
});

const markChoiceSchema = z.object({
  name: z.string(),
  index: z.number(),
  weight: z.number().int().min(1).max(4),
  excludeFromAi: z.boolean().optional().nullable(),
});

const examinerQuestionSchema = z.object({
  index: z.number(),
  question: z.string(),
});

export const markSchema = z.object({
  header: z.string(),
  choices: z.array(markChoiceSchema).min(1, 'Each header must have at least one choice'),
});

export const stationSchema = z
  .object({
    aiEnabled: z.boolean().optional().nullable(),
    aiConfig: z.custom<IAiConfig>().optional().nullable(),
    entitlementIds: z.array(z.string()),
    topicIds: z.array(z.string()),
    explanation: z.string(),
    actorBrief: z.string(),
    llmActorBrief: z.string().optional().nullable(),
    examinerBrief: z.string(),
    candidateBrief: z.string(),
    name: z.string(),
    hiddenName: z.string().optional().nullable(),
    enabled: z.string().optional().nullable(),
    difficulty: z.enum(EDifficultyType, { message: 'Difficulty is required' }),
    osceTypeId: z
      .enum(EOsceType, { message: 'OSCE Type is required' })
      .or(z.enum(EPaceType, { message: 'OSCE Type is required' })),
    assets: z.array(assetSchema).optional().nullable(),
    picture: pictureSchema.optional().nullable(),
    examinerQuestions: z.array(examinerQuestionSchema).optional().nullable(),
    // phaseBriefs: z.array(phaseBriefSchema).optional().nullable(),
    phaseTimers: phaseTimersSchema.optional().nullable(),
    marks: z
      .array(markSchema)
      .min(1, 'At least one mark is required')
      .superRefine((marks) => {
        const duplicates = marks
          .flatMap((mark, markIndex) =>
            mark.choices.map((choice, choiceIndex) => ({
              name: choice.name.trim().toLowerCase(),
              path: [markIndex, 'choices', choiceIndex, 'name'] as (string | number)[],
              params: {
                name: choice.name,
                header: mark.header,
              },
            })),
          )
          .reduce(
            (acc, { name, path, params }) => {
              acc[name] = [...(acc[name] || []), { path, params }];
              return acc;
            },
            {} as Record<string, { path: (string | number)[]; params: { name: string; header: string } }[]>,
          );

        const _duplicatePaths = Object.values(duplicates)
          .filter((paths) => paths.length > 1)
          .flat();

        // for (const { path, params } of duplicatePaths) {
        //   ctx.addIssue({
        //     code: 'custom',
        //     message: `Mark name must be unique\n\n Duplicate in HEADER: ${params.header} - CHOICE: ${params.name}`,
        //     path,
        //   });
        // }
      }),
  })
  .superRefine((data, ctx) => {
    // Pre-group assets by type for LightGallery fields
    const assetsByType = (data.assets || [])
      ?.filter((a) => !a.deleted)
      ?.reduce(
        (acc, asset) => {
          if (!asset) return acc;
          if (!acc[asset.type]) {
            acc[asset.type] = [];
          }
          acc[asset.type].push(asset);
          return acc;
        },
        {} as Record<ELightGalleryType, IValidateAsset['assets']>,
      );

    // LightGallery fields
    const fields: IValidateAsset[] = [
      {
        field: data.actorBrief,
        fieldName: 'actorBrief',
        path: ['actorBrief'],
        assets: assetsByType[ELightGalleryType.ACTOR] ?? [],
      },
      {
        field: data.examinerBrief,
        fieldName: 'examinerBrief',
        path: ['examinerBrief'],
        assets: assetsByType[ELightGalleryType.EXAMINER] ?? [],
      },
      {
        field: data.candidateBrief,
        fieldName: 'candidateBrief',
        path: ['candidateBrief'],
        assets: assetsByType[ELightGalleryType.CANDIDATE] ?? [],
      },
      {
        field: data.explanation,
        fieldName: 'explanation',
        path: ['explanation'],
        assets: assetsByType[ELightGalleryType.WALKTHROUGH] ?? [],
      },
    ];

    for (const f of fields) {
      const issues = validateFieldWithAssets(f);
      for (const issue of issues)
        ctx.addIssue({
          code: 'custom',
          path: issue.path,
          message: issue.message,
        });
    }
  });

export type IStationSchema = z.infer<typeof stationSchema>;
export type IMarkSchema = z.infer<typeof markSchema>;
export type IExaminerQuestionSchema = z.infer<typeof examinerQuestionSchema>;
export type IPhaseTimersSchema = z.infer<typeof phaseTimersSchema>;

/** @deprecated Phase briefs UI disabled — kept for legacy components */
export interface IPhaseBriefSchema {
  title: string;
  action: 'patient_agent' | 'examiner_agent' | 'next';
  timer: number;
  content: string;
}
